import React, { useState } from 'react';
import { useDocumentContext } from '../../../contexts/DocumentContext';
import { useManagerContext } from '../../../contexts/ManagerContext';
import DocumentTypesList from './DocumentTypesList';
import DocumentFieldsList from './DocumentFieldList';
import styles from '../../../styles/DocumentManagement.module.css';
import { DocumentSubcategoryItemProps } from '../../../types/DocumentManagement';

// Props interface now imported from types file

const DocumentSubcategoryItem: React.FC<DocumentSubcategoryItemProps> = React.memo(({ 
  subcategory, 
  stats, 
  isCompanyMode = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const documentContext = useDocumentContext();
  const managerContext = useManagerContext();
  
  // Use manager context for company mode, document context for user mode
  const {
    getDocumentTypesForSubcategory,
    getFieldsForSubcategory,
    expandedSubcategoryId
  } = isCompanyMode ? {
    // In company mode, get data from manager context
    getDocumentTypesForSubcategory: managerContext?.getCompanyDocumentTypesForSubcategory || (() => []),
    getFieldsForSubcategory: managerContext?.getCompanyFieldsForSubcategory || (() => []),
    expandedSubcategoryId: null
  } : {
    // In user mode, get data from document context
    getDocumentTypesForSubcategory: documentContext.getDocumentTypesForSubcategory,
    getFieldsForSubcategory: documentContext.getFieldsForSubcategory,
    expandedSubcategoryId: documentContext.expandedSubcategoryId
  };
  
  // Get types and fields using the appropriate methods
  const types = getDocumentTypesForSubcategory(subcategory.id);
  const fields = getFieldsForSubcategory(subcategory.id);

  React.useEffect(() => {
    if (expandedSubcategoryId === subcategory.id) {
      setIsExpanded(true);
    }
  }, [expandedSubcategoryId, subcategory.id]);

  const getCompletionColor = (rate: number) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'danger';
  };

  const getCompletionIcon = (rate: number) => {
    if (rate >= 90) return '✅';
    if (rate >= 70) return '⚠️';
    return '❌';
  };

  const getBarFillClass = (rate: number) => {
    if (rate >= 90) return styles.progressBarFillSuccess;
    if (rate >= 70) return styles.progressBarFillWarning;
    return styles.progressBarFillDanger;
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`${styles['doc-subcategory-card']} ${stats ? styles[`completion-${getCompletionColor(stats.completionRate)}`] : ''}`}>
      <div className={styles['doc-subcategory-header']} onClick={toggleExpanded}>
        <div className={styles['subcategory-info']}>
          <h5 className={styles['doc-subcategory-title']}>
            {subcategory.name}
          </h5>
        </div>
        <div className={styles['subcategory-actions']}>
          {stats && (
            <div className={styles['completion-summary']}>
              <span className={`${styles['completion-rate']} ${styles[`completion-${getCompletionColor(stats.completionRate)}`]}`}>
                {stats.completionRate.toFixed(1)}%
              </span>
              <div className={styles.miniProgressBarTrack}>
                <div
                  className={`${styles.miniProgressBarFill} ${getBarFillClass(stats.completionRate)}`}
                  style={{ width: `${Math.min(stats.completionRate, 100)}%` }}
                />
              </div>
              <span className={styles['completion-details']}>
                ({stats.completedItems}/{stats.totalItems})
              </span>
            </div>
          )}
          {stats && (
            <div className={styles['stats-summary']}>
              {stats.missingItems > 0 && (
                <span className={`${styles['stat-badge']} ${styles['missing']}`}>
                  {stats.missingItems} missing
                </span>
              )}
              {stats.expiredItems > 0 && (
                <span className={`${styles['stat-badge']} ${styles['expired']}`}>
                  {stats.expiredItems} expired
                </span>
              )}
            </div>
          )}
          <span className={`${styles['expand-arrow']} ${isExpanded ? styles['expanded'] : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className={styles['doc-subcategory-content']}>
          <DocumentTypesList 
            types={types}
            isCompanyMode={isCompanyMode}
          />
          <DocumentFieldsList 
            fields={fields}
            isCompanyMode={isCompanyMode}
          />
        </div>
      )}
    </div>
  );
});

// Add display name for debugging
DocumentSubcategoryItem.displayName = 'DocumentSubcategoryItem';

export default DocumentSubcategoryItem;
